import { placementBayOf } from "./basePogWalk";
import { isRowLevel, readHorizontalWall, rowLevelsOf, wallBranchOf } from "./horizontalBlocking";
import { deriveValueBands, sizeFamilyOf } from "@CommonUtils/cdtStudio/domain/sizeFamily";
import { packSizeTierAttrs, unitFamiliesOf } from "./sizeTiers";
import {
  Orientation,
  PATH_SEPARATOR,
  SpineLevel,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  type WallBlocking,
  type WallBlockingKind,
  branchStacksRows,
  isHorizontalWall,
  isPlaceholderValue,
  walkSplitScopes,
} from "./studioTypes";
import { compactRowsOf } from "./shelfRankSpace";

import type { AttributeCandidate, StudioPlacement } from "./studioTypes";

export type { AttributeCandidate };

export interface InferenceOptions {
  preferredOrder?: readonly string[];

  tieMargin?: number;

  verticalBlockShare?: number;

  /** Largest gap along a shelf, between two placements of one value, still read as one block:
   *  a percentage of the width of the bay the shelf belongs to (see `fragmentJoinOf`). */
  fragmentJoinPct?: number;

  /** A profile's join from before DEV-3670, a distance in its plan's authoring unit. Inference
   *  never reads it: `withPlanFragmentJoin` turns it into `fragmentJoinPct` against the plan. */
  legacyFragmentJoin?: number;

  firstPreferredValueMatch?: string | false;
  lastPreferredValueMatch?: string | false;
  firstPreferredValues?: Readonly<Record<string, readonly string[]>>;
  lastPreferredValues?: Readonly<Record<string, readonly string[]>>;

  trace?: (levelIndex: number, scored: readonly { attr: string; score: number }[]) => void;

  retainBands?: boolean;

  forcedOrder?: readonly SpineLevel[];

  /** Configured category hierarchy, from major to minor. */
  declaredLevelOrder?: readonly string[];

  rootAttr?: string;

  /** The quantity attribute (raw size). It SEQUENCES; it never takes a blocking rung while a
   *  candidate the preferred order ranks above it is still unplaced. Left in the contest, a
   *  many-valued size outscores a real block level (sub-brand) on fragments per value pair and
   *  buries it. A size TIER (Small / Regular / Family/Club) is a separate attribute and
   *  competes like any other: the tier blocks, the number orders within it. */
  quantityAttribute?: string;

  /** The profile's brand level, in the live namespace. The wall reads by the brand or a level
   *  above it, never by one inside it (`wallBranchOf`), and a configured root that splits the
   *  brands far finer gives the root to the brand. */
  brandAttr?: string;

  scoreBands?: Readonly<Record<string, Readonly<Record<string, string>>>>;

  quantize?: boolean;

  /** The merchant's reading of the wall, set when they flip inference's on an untouched
   *  draft. Inference follows it (walk order, row levels first) and still records what it
   *  read off the base pog as `wallBlocking.read`. */
  wallBlocking?: WallBlockingKind;
}

export const VERTICAL_BLOCK_SHARE = 0.55;

export const measureTopToBottomShare = (
  groups: readonly (readonly StudioItem[])[],
  shelvesPerRun: ReadonlyMap<number, number>,
): number => {
  let coveredWidth = 0;
  let totalWidth = 0;
  for (const group of groups) {
    const shelvesByRun = new Map<number, Set<number>>();
    let width = 0;
    for (const item of group) {
      for (const placement of item.placements) {
        const set = shelvesByRun.get(placement.run) ?? new Set<number>();
        set.add(placement.shelf);
        shelvesByRun.set(placement.run, set);
        width += placement.widthCm;
      }
    }
    let coverage = 0;
    let runsUsed = 0;
    shelvesByRun.forEach((set, run) => {
      coverage += set.size / Math.max(shelvesPerRun.get(run) ?? 1, 1);
      runsUsed += 1;
    });
    const fullHeight = runsUsed ? coverage / runsUsed : 0;
    totalWidth += width;
    coveredWidth += width * Math.min(fullHeight, 1);
  }
  return totalWidth > 0 ? coveredWidth / totalWidth : 0;
};

const DEFAULT_TIE_MARGIN = 0.12;
/** Root values per brand past which a configured root fragments the brands, not a level above them. */
const ROOT_FRAGMENTS_PER_BRAND = 2;

export const runOffsetsOf = (runs: readonly StudioRunGeometry[]): number[] => {
  const offsets: number[] = [];
  let acc = 0;
  for (const run of [...runs].sort((a, b) => a.run - b.run)) {
    offsets[run.run] = acc;
    acc += run.widthCm;
  }
  return offsets;
};

const laneSequences = (items: readonly StudioItem[]) => {
  const lanes = new Map<string, { xCm: number; item: StudioItem }[]>();
  for (const item of items) {
    for (const placement of item.placements) {
      const key = `${placement.run}|${placement.shelf}`;
      const lane = lanes.get(key);
      const entry = { xCm: placement.xCm, item };
      if (lane) lane.push(entry);
      else lanes.set(key, [entry]);
    }
  }
  for (const lane of lanes.values()) lane.sort((a, b) => a.xCm - b.xCm);
  return lanes;
};

interface Cleanliness {
  returns: number;

  inversions: number;
}

export const measureCleanliness = (items: readonly StudioItem[], attr: string): Cleanliness => {
  let returns = 0;
  let inversions = 0;
  const laneOrders: string[][] = [];
  for (const lane of laneSequences(items).values()) {
    const seen = new Set<string>();
    let previous: string | null = null;
    const firstSeen: string[] = [];
    for (const { item } of lane) {
      const value = item.attrs[attr] ?? "";
      if (value !== previous) {
        if (seen.has(value)) returns += 1;
        else {
          seen.add(value);
          firstSeen.push(value);
        }
        previous = value;
      }
    }
    laneOrders.push(firstSeen);
  }

  for (let a = 0; a < laneOrders.length; a++) {
    for (let b = a + 1; b < laneOrders.length; b++) {
      const rankB = new Map(laneOrders[b].map((value, index) => [value, index]));
      const shared = laneOrders[a].filter((value) => rankB.has(value));
      for (let i = 0; i < shared.length; i++) {
        for (let j = i + 1; j < shared.length; j++) {
          if ((rankB.get(shared[i]) ?? 0) > (rankB.get(shared[j]) ?? 0)) inversions += 1;
        }
      }
    }
  }
  return { returns, inversions };
};

/* The fragment join is a share of the shelf, not a distance, so it means the same on every plan,
   whatever unit the plan is drawn in and however many bays it has. "The shelf" is one bay's
   shelf: the bay width of the run the gap is on (`StudioRunGeometry.bayWidthCm`, the run's first
   bay; a run of mixed bay widths uses that one). */

/** 20 in of a 48 in bay: the join every Walmart profile was set against (see
 *  `legacyFragmentJoinPctOf`). */
export const DEFAULT_FRAGMENT_JOIN_PCT = (20 / 48) * 100;

/** The join in raw planogram units for a gap on the given run. */
export type FragmentJoin = (run: number) => number;

const bayWidthCmOf = (run: StudioRunGeometry): number =>
  run.bayWidthCm > 0 ? run.bayWidthCm : run.bays > 0 ? run.widthCm / run.bays : run.widthCm;

/** The configured join, per run, in raw planogram units. */
export const fragmentJoinOf = (
  runs: readonly StudioRunGeometry[],
  pct: number = DEFAULT_FRAGMENT_JOIN_PCT,
): FragmentJoin => {
  const joinCm = new Map(runs.map((run) => [run.run, (pct / 100) * bayWidthCmOf(run)]));
  return (run) => joinCm.get(run) ?? 0;
};

/** A plan whose bays are wider than this many raw units saved its legacy join in inches. */
const LEGACY_INCH_PLAN_WIDTH = 1000;

/** A pre-DEV-3670 join (`verticalBlocking.fragmentJoinCm`) as a share of the shelf of `bays`'
 *  plan. Whatever its key says, it was saved in the plan's own numbers on a plan of small numbers
 *  (bays at most 1000 wide), and in inches on a plan of large ones, whose raw units read as
 *  hundredths of an inch (unitless end to end, Wendy, 2026-10-29). On a 48 in bay it is
 *  value / 48. */
export const legacyFragmentJoinPctOf = (
  legacy: number,
  bays: readonly { width: number; height: number }[],
): number => {
  const rawBayWidth = bays[0]?.width ?? 0;
  if (!(rawBayWidth > 0)) return DEFAULT_FRAGMENT_JOIN_PCT;
  const unitsPerLegacy = rawBayWidth > LEGACY_INCH_PLAN_WIDTH ? 100 : 1;
  return (100 * legacy * unitsPerLegacy) / rawBayWidth;
};

/** `options` with a legacy join read as a share of the shelf of the plan it is inferred on. */
export const withPlanFragmentJoin = <T extends InferenceOptions>(
  options: T,
  bays: readonly { width: number; height: number }[],
): T & Pick<InferenceOptions, "fragmentJoinPct"> => {
  const { legacyFragmentJoin, ...rest } = options;
  if (legacyFragmentJoin === undefined) return options;
  return {
    ...rest,
    fragmentJoinPct: rest.fragmentJoinPct ?? legacyFragmentJoinPctOf(legacyFragmentJoin, bays),
  } as T;
};

/** On a shelf row where every bay holds one product (a row of pallets), products in
 *  neighbouring bays stand next to each other however wide the gap: the gap is the fixtures' unused width,
 *  not space between products. A pallet is one bay with one product (a 100 cm product on a
 *  122 cm pallet), so measured in centimetres alone the gap broke every block on a pallet
 *  planogram. A row with a bay holding several products keeps the centimetre rule. Read off the whole
 *  planogram by `markBayEdgeNeighbours`, so a scope's own items never see a gap as empty when
 *  another scope's product stands in it. */
const bayEdgeNeighbours = new WeakMap<StudioPlacement, Set<StudioPlacement>>();

export const markBayEdgeNeighbours = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): void => {
  const runOf = new Map(runs.map((run) => [run.run, run]));
  const bayOf = (placement: StudioPlacement) => placementBayOf(placement, runOf.get(placement.run));
  const lanes = new Map<string, StudioPlacement[]>();
  for (const item of items) {
    for (const placement of item.placements) {
      const key = `${placement.run}|${placement.shelf}`;
      const lane = lanes.get(key);
      if (lane) lane.push(placement);
      else lanes.set(key, [placement]);
    }
  }
  const link = (a: StudioPlacement, b: StudioPlacement) => {
    const set = bayEdgeNeighbours.get(a) ?? new Set<StudioPlacement>();
    set.add(b);
    bayEdgeNeighbours.set(a, set);
  };
  for (const lane of lanes.values()) {
    lane.sort((a, b) => a.xCm - b.xCm);
    const perBay = new Map<number, number>();
    for (const placement of lane) {
      perBay.set(bayOf(placement), (perBay.get(bayOf(placement)) ?? 0) + 1);
    }
    // A row of pallets: every bay on the row holds one product.
    if ([...perBay.values()].some((count) => count !== 1)) continue;
    for (let index = 0; index + 1 < lane.length; index += 1) {
      const left = lane[index];
      const right = lane[index + 1];
      if (bayOf(right) !== bayOf(left) + 1) continue;
      link(left, right);
      link(right, left);
    }
  }
};

export const measureExtraFragments = (
  items: readonly StudioItem[],
  attr: string,
  bandOf: Readonly<Record<string, string>> | undefined,
  join: FragmentJoin,
): number => {
  interface Box {
    run: number;
    shelf: number;
    x0: number;
    x1: number;
    placement: StudioPlacement;
    join: number;
  }
  const byValue = new Map<string, Box[]>();
  for (const item of items) {
    const raw = item.attrs[attr] ?? "";
    const value = bandOf?.[raw] ?? raw;
    const boxes = byValue.get(value) ?? [];
    for (const placement of item.placements) {
      boxes.push({
        run: placement.run,
        shelf: placement.shelf,
        x0: placement.xCm,
        x1: placement.xCm + placement.widthCm,
        placement,
        join: join(placement.run),
      });
    }
    byValue.set(value, boxes);
  }
  let extra = 0;
  byValue.forEach((boxes) => {
    const parent = boxes.map((_, index) => index);
    const find = (index: number): number =>
      parent[index] === index ? index : (parent[index] = find(parent[index]));
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i];
        const b = boxes[j];
        if (a.run !== b.run || Math.abs(a.shelf - b.shelf) > 1) continue;
        if (
          (a.x0 <= b.x1 + a.join && b.x0 <= a.x1 + a.join) ||
          (a.shelf === b.shelf && bayEdgeNeighbours.get(a.placement)?.has(b.placement))
        ) {
          parent[find(i)] = find(j);
        }
      }
    }
    const roots = new Set(boxes.map((_, index) => find(index)));
    extra += Math.max(roots.size - 1, 0);
  });
  return extra;
};

export const measureBlockShare = (
  items: readonly StudioItem[],
  attr: string,
  join: FragmentJoin,
): number => {
  interface Box {
    run: number;
    shelf: number;
    x0: number;
    x1: number;
    width: number;
    join: number;
  }
  const byValue = new Map<string, Box[]>();
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    const boxes = byValue.get(value) ?? [];
    for (const placement of item.placements) {
      boxes.push({
        run: placement.run,
        shelf: placement.shelf,
        x0: placement.xCm,
        x1: placement.xCm + placement.widthCm,
        width: placement.widthCm,
        join: join(placement.run),
      });
    }
    byValue.set(value, boxes);
  }
  let weighted = 0;
  let totalWidth = 0;
  byValue.forEach((boxes) => {
    const parent = boxes.map((_, index) => index);
    const find = (index: number): number =>
      parent[index] === index ? index : (parent[index] = find(parent[index]));
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i];
        const b = boxes[j];
        if (a.run !== b.run || Math.abs(a.shelf - b.shelf) > 1) continue;
        if (a.x0 <= b.x1 + a.join && b.x0 <= a.x1 + a.join) {
          parent[find(i)] = find(j);
        }
      }
    }
    const widthByRegion = new Map<number, number>();
    let valueWidth = 0;
    boxes.forEach((box, index) => {
      const root = find(index);
      widthByRegion.set(root, (widthByRegion.get(root) ?? 0) + box.width);
      valueWidth += box.width;
    });
    if (valueWidth <= 0) return;
    const largest = Math.max(...widthByRegion.values());
    weighted += largest;
    totalWidth += valueWidth;
  });
  return totalWidth > 0 ? weighted / totalWidth : 0;
};

type ValueOrderOptions = Pick<
  InferenceOptions,
  | "fragmentJoinPct"
  | "firstPreferredValueMatch"
  | "lastPreferredValueMatch"
  | "firstPreferredValues"
  | "lastPreferredValues"
>;

export const inferValueOrder = (
  items: readonly StudioItem[],
  attr: string,
  runs: readonly StudioRunGeometry[],
  options: ValueOrderOptions = {},
): string[] =>
  valueOrderByStretch(
    items,
    attr,
    runOffsetsOf(runs),
    fragmentJoinOf(runs, options.fragmentJoinPct),
    options,
  );

/** Values in the order of the centre of each one's largest joined stretch along the walk. */
const valueOrderByStretch = (
  items: readonly StudioItem[],
  attr: string,
  runOffsets: readonly number[],
  join: FragmentJoin,
  options: ValueOrderOptions,
): string[] => {
  const stretches = new Map<string, { start: number; end: number; width: number; run: number }[]>();
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    const list = stretches.get(value) ?? [];
    for (const placement of item.placements) {
      const start = runOffsets[placement.run] + placement.xCm;
      list.push({
        start,
        end: start + placement.widthCm,
        width: placement.widthCm,
        run: placement.run,
      });
    }
    stretches.set(value, list);
  }
  const anchors = new Map<string, number | null>();
  for (const [value, list] of stretches) {
    list.sort((a, b) => a.start - b.start);

    let best: { width: number; weighted: number } | null = null;
    // The run of the stretch reaching furthest right: a gap is judged by its left side's bay.
    let current = { end: -Infinity, width: 0, weighted: 0, run: 0 };
    const flush = () => {
      if (current.width > 0 && (best === null || current.width > best.width)) {
        best = { width: current.width, weighted: current.weighted };
      }
    };
    for (const stretch of list) {
      if (stretch.start - current.end > join(current.run)) {
        flush();
        current = { end: stretch.end, width: stretch.width, weighted: 0, run: stretch.run };
        current.weighted = ((stretch.start + stretch.end) / 2) * stretch.width;
      } else {
        if (stretch.end > current.end) current.run = stretch.run;
        current.end = Math.max(current.end, stretch.end);
        current.width += stretch.width;
        current.weighted += ((stretch.start + stretch.end) / 2) * stretch.width;
      }
    }
    flush();
    const winner = best as { width: number; weighted: number } | null;

    // Null anchor trails placed values; a mean would let overlays drag the order.
    anchors.set(value, winner ? winner.weighted / winner.width : null);
  }
  const measured = [...anchors.entries()]
    .sort((a, b) => (a[1] ?? Infinity) - (b[1] ?? Infinity))
    .map(([value, anchor]) => ({ value, anchor }));
  return applyPreferredValueRules(measured, attr, options);
};

const preferredValuePattern = (configured: string | false | undefined) =>
  typeof configured === "string" && configured ? new RegExp(configured, "i") : null;

/** FIRST is a tie-break only; LAST keeps override strength. */
export const houseRuleFirst = (
  values: readonly string[],
  options: Pick<InferenceOptions, "firstPreferredValueMatch" | "lastPreferredValueMatch"> = {},
): string[] => {
  const first = preferredValuePattern(options.firstPreferredValueMatch);
  const last = preferredValuePattern(options.lastPreferredValueMatch);
  const isFirst = (value: string) => first?.test(value.trim()) ?? false;
  const isLast = (value: string) => last?.test(value.trim()) ?? false;
  return [
    ...values.filter(isFirst),
    ...values.filter((value) => !isFirst(value) && !isLast(value)),
    ...values.filter((value) => !isFirst(value) && isLast(value)),
  ];
};

const valuesMatchingInRuleOrder = (
  values: readonly string[],
  rules: readonly string[],
): string[] => {
  const remaining = new Set(values);
  const ordered: string[] = [];
  for (const rule of rules) {
    const needle = rule.trim().toLowerCase();
    if (!needle) continue;
    for (const value of values) {
      if (remaining.has(value) && value.toLowerCase().includes(needle)) {
        ordered.push(value);
        remaining.delete(value);
      }
    }
  }
  return ordered;
};

interface MeasuredValue {
  value: string;
  anchor: number | null;
}

const ANCHOR_TIE_CM = 1e-6;

const applyPreferredValueRules = (
  measured: readonly MeasuredValue[],
  attr: string,
  options: Pick<
    InferenceOptions,
    | "firstPreferredValueMatch"
    | "lastPreferredValueMatch"
    | "firstPreferredValues"
    | "lastPreferredValues"
  >,
): string[] => {
  // FIRST only within tied anchors; LAST still overrides.
  const tieBroken: string[] = [];
  let group: MeasuredValue[] = [];
  const flushGroup = () => {
    if (group.length === 0) return;
    tieBroken.push(
      ...houseRuleFirst(
        group.map((entry) => entry.value),
        options,
      ),
    );
    group = [];
  };
  for (const entry of measured) {
    const previous = group[group.length - 1];
    const tied =
      previous !== undefined &&
      (previous.anchor === null
        ? entry.anchor === null
        : entry.anchor !== null && Math.abs(entry.anchor - previous.anchor) <= ANCHOR_TIE_CM);
    if (previous !== undefined && !tied) flushGroup();
    group.push(entry);
  }
  flushGroup();

  const last = preferredValuePattern(options.lastPreferredValueMatch);
  const isLast = (value: string) => last?.test(value.trim()) ?? false;
  const regexOrdered = [
    ...tieBroken.filter((value) => !isLast(value)),
    ...tieBroken.filter(isLast),
  ];

  const first = valuesMatchingInRuleOrder(regexOrdered, options.firstPreferredValues?.[attr] ?? []);
  const firstSet = new Set(first);
  const afterFirst = regexOrdered.filter((value) => !firstSet.has(value));
  const lastListed = valuesMatchingInRuleOrder(
    afterFirst,
    options.lastPreferredValues?.[attr] ?? [],
  );
  const lastSet = new Set(lastListed);
  return [...first, ...afterFirst.filter((value) => !lastSet.has(value)), ...lastListed];
};

export const inferReadingOrder = (items: readonly StudioItem[], attr: string): string[] => {
  const entries: { run: number; shelf: number; xCm: number; value: string }[] = [];
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    for (const placement of item.placements) {
      entries.push({ run: placement.run, shelf: placement.shelf, xCm: placement.xCm, value });
    }
  }
  entries.sort((a, b) => a.run - b.run || a.shelf - b.shelf || a.xCm - b.xCm);
  const seen = new Set<string>();
  const order: string[] = [];
  for (const entry of entries) {
    if (!seen.has(entry.value)) {
      seen.add(entry.value);
      order.push(entry.value);
    }
  }
  return order;
};

/** Value order on a horizontally blocked wall: the base pog walk (run, bay, shelf from the
 *  top, left to right), each value anchored where its largest block starts. Reading x alone
 *  flattens the rows into one strip, so a brand at the left of the bottom shelf would lead
 *  every brand above it; the walk reads bay 1 top to bottom first. A stray facing is a
 *  block of its own and cannot pull its value forward past the main block. */
export const inferWalkValueOrder = (
  items: readonly StudioItem[],
  attr: string,
  runs: readonly StudioRunGeometry[],
  options: Pick<
    InferenceOptions,
    | "firstPreferredValueMatch"
    | "lastPreferredValueMatch"
    | "firstPreferredValues"
    | "lastPreferredValues"
  > = {},
): string[] => {
  const runOf = new Map(runs.map((run) => [run.run, run]));
  const facings: { key: readonly number[]; value: string; widthCm: number }[] = [];
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    for (const placement of item.placements) {
      const bay = placementBayOf(placement, runOf.get(placement.run));
      facings.push({
        key: [placement.run, bay, placement.shelf, placement.xCm],
        value,
        widthCm: placement.widthCm,
      });
    }
  }
  facings.sort((a, b) => {
    for (let index = 0; index < a.key.length; index += 1) {
      const diff = a.key[index] - b.key[index];
      if (diff !== 0) return diff;
    }
    return 0;
  });
  // Runs of one value along the walk are its blocks; the widest one anchors it.
  const best = new Map<string, { widthCm: number; start: number }>();
  for (let start = 0; start < facings.length; ) {
    let end = start;
    let widthCm = 0;
    while (end < facings.length && facings[end].value === facings[start].value) {
      widthCm += facings[end].widthCm;
      end += 1;
    }
    const value = facings[start].value;
    const current = best.get(value);
    if (!current || widthCm > current.widthCm) best.set(value, { widthCm, start });
    start = end;
  }
  const measured: MeasuredValue[] = [...best.entries()]
    .sort((a, b) => a[1].start - b[1].start)
    .map(([value, block]) => ({ value, anchor: block.start }));
  // Values with no placement trail, in first-seen order.
  const placed = new Set(best.keys());
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined || placed.has(value)) continue;
    placed.add(value);
    measured.push({ value, anchor: null });
  }
  return applyPreferredValueRules(measured, attr, options);
};

export const inferVerticalValueOrder = (
  items: readonly StudioItem[],
  attr: string,
  runOffsets: readonly number[],
): string[] => {
  const totals = new Map<string, { shelfWeighted: number; xWeighted: number; width: number }>();
  for (const item of items) {
    const value = item.attrs[attr];
    if (value === null || value === undefined) continue;
    for (const placement of item.placements) {
      const weight = placement.widthCm;
      const flowX = runOffsets[placement.run] + placement.xCm + placement.widthCm / 2;
      const total = totals.get(value);
      if (total) {
        total.shelfWeighted += placement.shelf * weight;
        total.xWeighted += flowX * weight;
        total.width += weight;
      } else {
        totals.set(value, {
          shelfWeighted: placement.shelf * weight,
          xWeighted: flowX * weight,
          width: weight,
        });
      }
    }
  }

  return [...totals.entries()]
    .sort((a, b) => {
      const shelfDelta = a[1].shelfWeighted / a[1].width - b[1].shelfWeighted / b[1].width;
      if (Math.abs(shelfDelta) > 0.5) return shelfDelta;
      return a[1].xWeighted / a[1].width - b[1].xWeighted / b[1].width;
    })
    .map(([value]) => value);
};

interface OrientationMeasure {
  orientation: Orientation;

  confidence: number;

  xSeparation: number;

  shelfSeparation: number;
}

export const measureOrientation = (
  groups: readonly (readonly StudioItem[])[],
  runOffsets: readonly number[],
): OrientationMeasure => {
  const extents = groups.map((group) => {
    let min = Infinity;
    let max = -Infinity;
    const shelves = new Set<string>();
    for (const item of group) {
      for (const placement of item.placements) {
        const start = runOffsets[placement.run] + placement.xCm;
        min = Math.min(min, start);
        max = Math.max(max, start + placement.widthCm);
        shelves.add(`${placement.run}|${placement.shelf}`);
      }
    }
    return { min, max, shelves };
  });
  let xOverlap = 0;
  let xSpan = 0;
  let shelfShared = 0;
  let shelfPairs = 0;
  for (let i = 0; i < extents.length; i++) {
    for (let j = i + 1; j < extents.length; j++) {
      const a = extents[i];
      const b = extents[j];
      const overlap = Math.max(0, Math.min(a.max, b.max) - Math.max(a.min, b.min));
      xOverlap += overlap;
      xSpan += Math.min(a.max - a.min, b.max - b.min);
      const shared = [...a.shelves].filter((shelf) => b.shelves.has(shelf)).length;
      shelfShared += shared;
      shelfPairs += Math.min(a.shelves.size, b.shelves.size);
    }
  }
  const xSeparation = xSpan > 0 ? 1 - xOverlap / xSpan : 1;
  const shelfSeparation = shelfPairs > 0 ? 1 - shelfShared / shelfPairs : 0;
  if (shelfSeparation > xSeparation) {
    return {
      orientation: "vertical",
      confidence: shelfSeparation - xSeparation,
      xSeparation,
      shelfSeparation,
    };
  }
  return {
    orientation: "horizontal",
    confidence: xSeparation - shelfSeparation,
    xSeparation,
    shelfSeparation,
  };
};

interface ScopedGroup {
  path: readonly string[];
  items: readonly StudioItem[];
}

const splitScope = (scope: ScopedGroup, attr: string, order: readonly string[]): ScopedGroup[] => {
  const groups = new Map<string, StudioItem[]>();
  for (const item of scope.items) {
    const value = item.attrs[attr] ?? "";
    const group = groups.get(value);
    if (group) group.push(item);
    else groups.set(value, [item]);
  }
  const rank = new Map(order.map((value, index) => [value, index]));
  return [...groups.entries()]
    .sort((a, b) => (rank.get(a[0]) ?? order.length) - (rank.get(b[0]) ?? order.length))
    .map(([value, items]) => ({ path: [...scope.path, value], items }));
};

const NEAR_CONSTANT_SHARE = 0.8;

const BRANCH_BLOCK_MARGIN = 0.1;

const preferredRankOf = (preferred: readonly string[]) => (attr: string) => {
  const index = preferred.indexOf(attr);
  return index < 0 ? preferred.length : index;
};

/** A candidate whose values fragment more often than they split is not a block level: it
 *  cannot hold the quantity attribute back. Fragments per value pair, so -1 is "one stray
 *  stretch per value" — Detergent's sub-brand (55 strays over 31 values) fails this, Fabric
 *  care's (2 strays over 19 values) passes. Judged at SECTION level, not at the depth where
 *  size is contested: deep inside brand › form › pack type the scopes are tiny and almost
 *  anything looks contiguous there. */
export const BLOCK_WORTHY_SCORE = -1;

/** Fragments per value pair for one attribute over one group; null when it does not split. */
const fragmentScoreOf = (
  group: readonly StudioItem[],
  attr: string,
  bandOf: Readonly<Record<string, string>> | undefined,
  join: FragmentJoin,
): number | null => {
  // Items that carry no value for this attribute are not a block of their own: a filler
  // ("Original" as no sub-brand, once blanked) scattered along the shelf must not count as
  // fragments against the attribute that the real values partition cleanly.
  const values = new Set<string>();
  const carrying: StudioItem[] = [];
  for (const item of group) {
    const raw = item.attrs[attr];
    if (raw === null || raw === undefined || isPlaceholderValue(raw)) continue;
    carrying.push(item);
    values.add(bandOf?.[raw] ?? raw);
  }
  if (values.size < 2) return null;
  return -measureExtraFragments(carrying, attr, bandOf, join) / (values.size - 1);
};

/** How far one attribute's grouping of one scope is from chance: the fragments a random order
 *  of the same products would give (expected count and its variance, the multi-value runs
 *  test) against the fragments the base shows. Products without a value are left out, as in
 *  `fragmentScoreOf`. Null when the attribute does not split the scope. */
interface GroupingMoments {
  /** Expected fragments under a random order minus the fragments the base shows. */
  gap: number;
  variance: number;
  /** Fragments beyond one per value: 0 when every value sits together. */
  extra: number;
  groups: number;
}

const groupingMomentsOf = (
  group: readonly StudioItem[],
  attr: string,
  bandOf: Readonly<Record<string, string>> | undefined,
  join: FragmentJoin,
): GroupingMoments | null => {
  const counts = new Map<string, number>();
  const carrying: StudioItem[] = [];
  for (const item of group) {
    const raw = item.attrs[attr];
    if (raw === null || raw === undefined || isPlaceholderValue(raw)) continue;
    carrying.push(item);
    const value = bandOf?.[raw] ?? raw;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  if (counts.size < 2) return null;
  const m = carrying.length;
  let squares = 0;
  let cubes = 0;
  for (const count of counts.values()) {
    squares += count * count;
    cubes += count * count * count;
  }
  const extra = measureExtraFragments(carrying, attr, bandOf, join);
  const expected = m + 1 - squares / m;
  const variance =
    (squares * (squares + m * (m + 1)) - 2 * m * cubes - m * m * m) / (m * m * (m - 1));
  return {
    gap: expected - (counts.size + extra),
    variance: Math.max(variance, 0),
    extra,
    groups: counts.size,
  };
};

/** The evidence the base gives for an attribute over a set of scopes, with what the contest
 *  needs to break ties. `evidence` is in standard deviations: how many fewer fragments the
 *  base shows than a random order of the same products would. An attribute with a value per
 *  product has no variance and so no evidence, however tidy it looks; a coarse attribute
 *  whose values sit together has a lot. */
interface GroupingEvidence {
  evidence: number;
  extra: number;
  groups: number;
}

const groupingEvidenceOf = (
  scopes: readonly (readonly StudioItem[])[],
  attr: string,
  bandOf: Readonly<Record<string, string>> | undefined,
  join: FragmentJoin,
): GroupingEvidence => {
  let gap = 0;
  let variance = 0;
  let extra = 0;
  let groups = 0;
  for (const scope of scopes) {
    const moments = groupingMomentsOf(scope, attr, bandOf, join);
    if (!moments) continue;
    gap += moments.gap;
    variance += moments.variance;
    extra += moments.extra;
    groups += moments.groups;
  }
  return { evidence: variance > 0 ? gap / Math.sqrt(variance) : 0, extra, groups };
};

interface RungEntry extends GroupingEvidence {
  attr: string;
  /** The same measure over the whole planogram: the last word between attributes that group
   *  this rung's products identically. */
  overall: number;
}

/** An attribute that splits the rung before one that does not; one the base keeps whole before
 *  one it splits; between two it keeps whole, the coarser (fewer groups), which holds the finer
 *  one inside it; then more evidence; then the evidence over the whole planogram; the
 *  attribute key only for a perfect tie. Never the column order. */
const byEvidence = (a: RungEntry, b: RungEntry): number =>
  Number(a.groups === 0) - Number(b.groups === 0) ||
  Number(a.extra > 0) - Number(b.extra > 0) ||
  a.groups - b.groups ||
  b.evidence - a.evidence ||
  b.overall - a.overall ||
  a.attr.localeCompare(b.attr);

/** The rung's winner among its contenders. The configured order decides first, as before;
 *  attributes it does not name share the last rank and are ordered by evidence, so the
 *  attributes API's key order never decides.
 *
 *  One exception, read off the base: a configured level the base splits at this rung yields to
 *  a contender that is a level ABOVE it on the base, that is, one the base keeps whole, that
 *  groups no finer, that the base gives more evidence for, and along whose boundaries every
 *  split of the configured level falls. On Sam's Club laundry Tide stands in two places, liquid
 *  and pods; pack format is whole, has two values, and Tide is whole inside each, so pack format
 *  goes above brand. */
const pickByEvidence = <T extends { candidate: { attr: string } }>(
  contenders: readonly T[],
  preferredIndex: (attr: string) => number,
  scopes: readonly (readonly StudioItem[])[],
  measure: (scopes: readonly (readonly StudioItem[])[], attr: string) => GroupingEvidence,
  overall: (attr: string) => number,
  /** Off for a branch's own order, which is adopted only where it beats the tree's fragments. */
  yieldToLevelAbove = true,
): T => {
  const entries = new Map<string, RungEntry>(
    contenders.map((entry) => {
      const attr = entry.candidate.attr;
      return [attr, { attr, ...measure(scopes, attr), overall: overall(attr) }];
    }),
  );
  const entryOf = (entry: T) => entries.get(entry.candidate.attr)!;
  const ranked = [...contenders].sort(
    (a, b) =>
      preferredIndex(a.candidate.attr) - preferredIndex(b.candidate.attr) ||
      byEvidence(entryOf(a), entryOf(b)),
  );
  const first = ranked[0];
  const held = entryOf(first);
  if (!yieldToLevelAbove || held.extra === 0) return first;
  const above = ranked
    .filter((entry) => {
      const evidence = entryOf(entry);
      return (
        evidence.groups > 0 &&
        evidence.extra === 0 &&
        evidence.groups <= held.groups &&
        evidence.evidence > held.evidence &&
        measure(splitScopesBy(scopes, entry.candidate.attr), held.attr).extra === 0
      );
    })
    .sort((a, b) => byEvidence(entryOf(a), entryOf(b)));
  return above[0] ?? first;
};

/** Block-worthiness across the root sections, weighted by items: an attribute that is a clean
 *  block in the sections holding most of the planogram may hold size back; one that is a
 *  jumble there may not, however tidy it looks deep in the tree. */
const sectionBlockWorthy = (
  items: readonly StudioItem[],
  rootAttr: string,
  attr: string,
  bandOf: Readonly<Record<string, string>> | undefined,
  join: FragmentJoin,
): boolean => {
  const sections = new Map<string, StudioItem[]>();
  for (const item of items) {
    const key = String(item.attrs[rootAttr] ?? "");
    const list = sections.get(key);
    if (list) list.push(item);
    else sections.set(key, [item]);
  }
  let weighted = 0;
  let weight = 0;
  for (const group of sections.values()) {
    const score = fragmentScoreOf(group, attr, bandOf, join);
    if (score === null) continue;
    weighted += score * group.length;
    weight += group.length;
  }
  return weight > 0 && weighted / weight > BLOCK_WORTHY_SCORE;
};

/** Candidates allowed to win the next rung. The quantity attribute is held back while any
 *  other remaining BLOCK-WORTHY candidate sits above it in the preferred order; attributes
 *  the order does not name rank last and never hold it back. */
export const rungContenders = <T extends { attr: string }>(
  remaining: readonly T[],
  _preferred: readonly string[],
  quantity: string | undefined,
  blockWorthy: (attr: string) => boolean = () => true,
): T[] => {
  if (quantity === undefined || !remaining.some((candidate) => candidate.attr === quantity)) {
    return [...remaining];
  }
  // Raw size is never a tier. It waits until no block-worthy level is left, wherever the
  // preferred order lists it: a size TIER, flavour or sub-brand that genuinely blocks the
  // shelf all come first; a jumbled attribute does not hold it back.
  const heldBack = remaining.some(
    (candidate) => candidate.attr !== quantity && blockWorthy(candidate.attr),
  );
  return heldBack ? remaining.filter((candidate) => candidate.attr !== quantity) : [...remaining];
};

/** Whether nesting upper first would hide the declared lower level. */
const dropsDeclaredLevel = (
  declared: readonly string[] | undefined,
  scopes: readonly (readonly StudioItem[])[],
  upper: string,
  lower: string,
): boolean => {
  if (!declared) return false;
  const upperRank = declared.indexOf(upper);
  const lowerRank = declared.indexOf(lower);
  if (upperRank < 0 || lowerRank < 0 || upperRank < lowerRank) return false;
  let splits = false;
  for (const scope of scopes) {
    const lowerOfUpper = new Map<string, string>();
    const lowerValues = new Set<string>();
    for (const item of scope) {
      const lowerValue = item.attrs[lower] ?? "";
      lowerValues.add(lowerValue);
      const upperValue = item.attrs[upper] ?? "";
      const seen = lowerOfUpper.get(upperValue);
      if (seen === undefined) lowerOfUpper.set(upperValue, lowerValue);
      else if (seen !== lowerValue) return false;
    }
    if (lowerValues.size > 1) splits = true;
  }
  return splits;
};

/** Removes rung choices that would hide a declared level, when possible. */
const declaredContenders = <T extends { candidate: { attr: string } }>(
  contenders: readonly T[],
  remaining: readonly { attr: string }[],
  declared: readonly string[] | undefined,
  scopes: readonly (readonly StudioItem[])[],
): T[] => {
  if (!declared || declared.length < 2) return [...contenders];
  const kept = contenders.filter(
    ({ candidate }) =>
      !remaining.some(
        (other) =>
          other.attr !== candidate.attr &&
          dropsDeclaredLevel(declared, scopes, candidate.attr, other.attr),
      ),
  );
  return kept.length > 0 ? kept : [...contenders];
};

const splitScopesBy = (scopes: readonly (readonly StudioItem[])[], attr: string): StudioItem[][] =>
  scopes.flatMap((scope) => {
    const groups = new Map<string, StudioItem[]>();
    for (const item of scope) {
      const value = item.attrs[attr] ?? "";
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    return groups.size < 2 ? [[...scope]] : [...groups.values()];
  });

/** A level blank for more than this share of a branch's products is sparse there. */
export const SPARSE_LEVEL_SHARE = 0.5;

const blankShareOf = (branchItems: readonly StudioItem[], attr: string): number =>
  branchItems.length === 0
    ? 1
    : branchItems.filter((item) => isPlaceholderValue(String(item.attrs[attr] ?? ""))).length /
      branchItems.length;

/**
 * Re-seats the levels that are blank for most of the branch's products and that the ranking put
 * ahead of every filled level, where the plan puts them among the filled ones. The branch
 * ranking can lift such a level to the top when the few products that carry it stand as a clean
 * column (nineteen Tide pods with a sub-brand of "Regular" or "Power" on base planogram 210),
 * which then splits every product of the branch by that level before the brand. A level most
 * products lack says nothing about how the branch is blocked, so it never leads; once a filled
 * level leads, a sparse level below it stays where the ranking put it (deodorant 188's form,
 * which its release before DEV-3564 ships where the ranking put it).
 */
export const seatSparseLevels = (
  attrs: readonly string[],
  planOrder: readonly string[],
  branchItems: readonly StudioItem[],
): string[] => {
  const isSparse = (attr: string) => blankShareOf(branchItems, attr) > SPARSE_LEVEL_SHARE;
  const firstFilled = attrs.findIndex((attr) => !isSparse(attr));
  const sparse = firstFilled < 0 ? [] : attrs.slice(0, firstFilled);
  if (sparse.length === 0) return [...attrs];
  const planRank = new Map(planOrder.map((attr, index) => [attr, index]));
  const seated = attrs.filter((attr) => !sparse.includes(attr));
  for (const attr of [...sparse].sort(
    (a, b) => (planRank.get(a) ?? Infinity) - (planRank.get(b) ?? Infinity),
  )) {
    // After the last filled level the plan puts before it.
    const rank = planRank.get(attr) ?? Infinity;
    let at = 0;
    seated.forEach((entry, index) => {
      if ((planRank.get(entry) ?? Infinity) < rank) at = index + 1;
    });
    seated.splice(at, 0, attr);
  }
  return seated;
};

/** A column restates the quantity when its number is the pack size on this share of products. */
const QUANTITY_RESTATED_SHARE = 0.9;

/** One label restates another's size: the same amount in one unit family ("1 L" is "1000 ml"),
 *  or the same number where one side spells no unit ("12 Mega" on "12 ct"). A count never
 *  restates a volume ("12 ct" is not "12 fl oz"). */
const restatesSize = (label: string | null | undefined, size: string | null | undefined) => {
  const left = sizeFamilyOf(String(label ?? ""));
  const right = sizeFamilyOf(String(size ?? ""));
  if (left.family === "bare" || right.family === "bare")
    return leadingNumberOf(label) === leadingNumberOf(size);
  if (left.family !== right.family) return false;
  return left.magnitude.toPrecision(6) === right.magnitude.toPrecision(6);
};

const leadingNumberOf = (label: string | null | undefined): number =>
  parseFloat(/-?\d+(?:\.\d+)?/.exec(String(label ?? ""))?.[0] ?? "0") || 0;

/** The levels besides the quantity that speak pack size, each with its distinct value count on
 *  the wall: size tiers, and columns that restate the quantity ("12 Mega" on a 12-roll pack) —
 *  not every column with a number in it ("2-ply"). */
export const sizeLevelsOf = (
  items: readonly StudioItem[],
  candidates: readonly AttributeCandidate[],
  quantity: string | undefined,
): ReadonlyMap<string, number> => {
  const tiers = packSizeTierAttrs(items);
  // One family to compare in: counts never restate volumes, whatever the numbers say.
  const oneFamily = unitFamiliesOf(items).size <= 1;
  const sizeOf = (value: string | null | undefined) => sizeFamilyOf(String(value ?? ""));
  const restatesQuantity = (attr: string): boolean => {
    if (quantity === undefined || !oneFamily) return false;
    const both = items.filter(
      (item) =>
        sizeOf(item.attrs[attr]).magnitude > 0 && sizeOf(item.attrs[quantity]).magnitude > 0,
    );
    const agree = both.filter((item) => restatesSize(item.attrs[attr], item.attrs[quantity]));
    return both.length > 0 && agree.length >= QUANTITY_RESTATED_SHARE * both.length;
  };
  const levels = new Map<string, number>();
  for (const { attr } of candidates) {
    if (attr === quantity || !(tiers.has(attr) || restatesQuantity(attr))) continue;
    const values = items.map((item) => String(item.attrs[attr] ?? ""));
    levels.set(attr, new Set(values.filter((value) => !isPlaceholderValue(value))).size);
  }
  return levels;
};

/** Re-seats the quantity attribute in an order a cost search may have lifted it in: directly
 *  after the last block-worthy level. The levels that speak pack size in other words (a size
 *  tier, a "12 Mega" pack size) go with it, coarsest first: pack size sits below the sub-line. */
export const pinQuantity = (
  order: readonly string[],
  quantity: string | undefined,
  blockWorthy: (attr: string) => boolean = () => true,
  sizeLevels: ReadonlyMap<string, number> = new Map(),
): string[] => {
  const pinned = order.filter((attr) => attr === quantity || sizeLevels.has(attr));
  if (pinned.length === 0) return [...order];
  // Coarse to fine by distinct values; the raw quantity is the finest.
  const fineness = (attr: string) =>
    attr === quantity ? Number.POSITIVE_INFINITY : (sizeLevels.get(attr) ?? 0);
  pinned.sort((a, b) => fineness(a) - fineness(b));
  const rest = order.filter((attr) => !pinned.includes(attr));
  let at = -1;
  rest.forEach((attr, index) => {
    if (blockWorthy(attr)) at = index;
  });
  return [...rest.slice(0, at + 1), ...pinned, ...rest.slice(at + 1)];
};

const rankBranch = (
  branchItems: readonly StudioItem[],
  pool: readonly AttributeCandidate[],
  preferred: readonly string[],
  join: FragmentJoin,
  quantity?: string,
  declared?: readonly string[],
  /** The whole planogram, for the evidence that breaks a tie the branch cannot. */
  allItems: readonly StudioItem[] = branchItems,
): { attrs: string[]; decisive: boolean[] } | null => {
  if (branchItems.length === 0) return null;
  const preferredIndex = preferredRankOf(preferred);
  let scopes: readonly StudioItem[][] = [[...branchItems]];
  const remaining = [...pool];
  const attrs: string[] = [];
  const decisive: boolean[] = [];
  while (remaining.length > 0) {
    // Scored on the spine's own measure — extra shelf fragments per value pair — not on
    // block share. Block share saturates on a real aisle (every candidate 0.88–0.99), the
    // margin swallows the differences and the preferred order decides every rung, so a
    // section never gets an order of its own. Fragments per pair separates a level that
    // partitions the section (Fabric care sub-brand, 2 strays over 19 values) from one that
    // does not. The quantity attribute is kept out of the contest (see `rungContenders`).
    const scored = remaining.map((candidate) => {
      let share = 0;
      let weight = 0;
      let extra = 0;
      let valuePairs = 0;
      let usable = false;
      for (const scope of scopes) {
        if (scope.length <= 1) continue;

        weight += scope.length;
        const counts = new Map<string, number>();
        for (const item of scope) {
          const value = item.attrs[candidate.attr];
          if (value === null || value === undefined) continue;
          counts.set(value, (counts.get(value) ?? 0) + 1);
        }
        const carried = [...counts.values()].reduce((a, b) => a + b, 0);
        if (counts.size < 2 || carried === 0) continue;
        const biggest = Math.max(...counts.values());
        if (biggest / carried >= NEAR_CONSTANT_SHARE) continue;

        if (carried >= MIN_ITEMS_TO_JUDGE_GENERICITY && counts.size / carried > NEAR_UNIQUE_RATIO) {
          continue;
        }
        usable = true;
        if (quantity === undefined) {
          share += measureBlockShare(scope, candidate.attr, join) * scope.length;
        } else {
          extra += measureExtraFragments(scope, candidate.attr, undefined, join);
          valuePairs += counts.size - 1;
        }
      }
      // Without a pinned quantity attribute the fragment measure would let a many-valued size
      // take a rung (Detergent: brand > form > size > pack type), so callers that configure no
      // quantity keep the block-share ranking they were built against.
      return {
        candidate,
        // Unusable candidates sit below every usable one; ties among them fall to preference.
        score:
          quantity === undefined
            ? usable && weight > 0
              ? share / weight
              : -1
            : usable
              ? -extra / Math.max(valuePairs, 1)
              : Number.NEGATIVE_INFINITY,
        usable,
      };
    });
    const eligible = new Set(
      rungContenders(
        remaining,
        preferred,
        quantity,
        // Any level that still splits this branch comes before raw size.
        (attr) => scored.find((entry) => entry.candidate.attr === attr)?.usable ?? false,
      ).map((candidate) => candidate.attr),
    );
    const inContest = declaredContenders(
      scored.filter((entry) => eligible.has(entry.candidate.attr)),
      remaining,
      declared,
      scopes,
    );
    const best = inContest.reduce((a, b) => (b.score > a.score ? b : a));
    // With a pinned quantity the merchant's declared order decides the nesting among the
    // levels that are block-worthy here (fewer strays than value pairs); the measure only
    // demotes a jumble. Contiguity cannot tell "sub-brand blocks holding scents" from "scent
    // blocks holding sub-brands" — both read clean — so a flavour with many values must not
    // outrank the sub-brand the profile puts above it on fragments-per-pair alone.
    const worthy =
      quantity === undefined
        ? []
        : inContest.filter((entry) => entry.usable && entry.score > BLOCK_WORTHY_SCORE);
    const contenders =
      worthy.length > 0
        ? worthy
        : inContest.filter((entry) => entry.score >= best.score - BRANCH_BLOCK_MARGIN);
    const winner = pickByEvidence(
      contenders,
      preferredIndex,
      scopes,
      (rungScopes, attr) => groupingEvidenceOf(rungScopes, attr, undefined, join),
      (attr) => groupingEvidenceOf([allItems], attr, undefined, join).evidence,
      false,
    );
    attrs.push(winner.candidate.attr);
    decisive.push(winner.usable && contenders.length === 1);
    remaining.splice(
      remaining.findIndex((candidate) => candidate.attr === winner.candidate.attr),
      1,
    );
    scopes = scopes.flatMap((scope) => {
      const groups = new Map<string, StudioItem[]>();
      for (const item of scope) {
        const value = item.attrs[winner.candidate.attr] ?? "";
        const group = groups.get(value);
        if (group) group.push(item);
        else groups.set(value, [item]);
      }
      return groups.size < 2 ? [scope] : [...groups.values()];
    });
  }
  return { attrs, decisive };
};

const treeCost = (
  branchItems: readonly StudioItem[],
  order: readonly string[],
  join: FragmentJoin,
): number => {
  let cost = 0;
  let scopes: readonly StudioItem[][] = [[...branchItems]];
  for (const attr of order) {
    for (const scope of scopes) {
      if (scope.length <= 1) continue;
      if (new Set(scope.map((item) => item.attrs[attr] ?? "")).size < 2) continue;
      cost += measureExtraFragments(scope, attr, undefined, join);
    }
    scopes = scopes.flatMap((scope) => {
      const groups = new Map<string, StudioItem[]>();
      for (const item of scope) {
        const value = item.attrs[attr] ?? "";
        const group = groups.get(value);
        if (group) group.push(item);
        else groups.set(value, [item]);
      }
      return groups.size < 2 ? [scope] : [...groups.values()];
    });
  }
  return cost;
};

const refineBranchOrder = (
  branchItems: readonly StudioItem[],
  attrs: readonly string[],
  join: FragmentJoin,
  declared?: readonly string[],
): { attrs: string[]; cost: number } => {
  let best = [...attrs];
  let bestCost = treeCost(branchItems, best, join);
  let improved = true;
  while (improved && bestCost > 0) {
    improved = false;
    for (let index = 0; index + 1 < best.length; index += 1) {
      // Keep a configured level ahead of a child that cannot split it.
      if (
        declared?.includes(best[index]) &&
        declared.includes(best[index + 1]) &&
        dropsDeclaredLevel(
          declared,
          best.slice(0, index).reduce(splitScopesBy, [[...branchItems]]),
          best[index + 1],
          best[index],
        )
      ) {
        continue;
      }
      const swapped = [...best];
      [swapped[index], swapped[index + 1]] = [swapped[index + 1], swapped[index]];
      const cost = treeCost(branchItems, swapped, join);
      if (cost < bestCost) {
        best = swapped;
        bestCost = cost;
        improved = true;
      }
    }
  }
  return { attrs: best, cost: bestCost };
};

export interface InferredSpine {
  spine: SpineLevel[];

  scopedLevelOrder: Record<string, string[]>;
  valueOrder: Record<string, string[]>;
  scopedValueOrder: Record<string, string[]>;
  bandOverrides: Record<string, number[]>;
  orientOverrides: Record<string, Orientation>;
  shelfHints: Record<string, { shelf: number; hFacings: number }[]>;

  levelBands: Record<string, Record<string, string>>;
  /** How the base pog blocks, on any wall with a level to read it by; see `StudioDraft.wallBlocking`. */
  wallBlocking?: WallBlocking;
  /** The quantity level inference was given (`InferenceOptions.quantityAttribute`); see
   *  `StudioDraft.quantityAttribute`. */
  quantityAttribute?: string;
}

export const NEAR_UNIQUE_RATIO = 0.25;

export const MIN_ITEMS_TO_JUDGE_GENERICITY = 32;

const distinctValueRatio = (
  items: readonly StudioItem[],
  attr: string,
  bandOf?: Readonly<Record<string, string>>,
): number => {
  const values = new Set<string>();
  let carried = 0;
  for (const item of items) {
    const raw = item.attrs[attr];
    if (raw == null) continue;
    carried += 1;
    values.add(bandOf?.[raw] ?? raw);
  }
  return carried > 0 ? values.size / carried : 0;
};

export interface LevelQuantization {
  levelBands: Record<string, Record<string, string>>;

  excluded: readonly string[];
}

/** Items a value needs before it can be a block of its own. */
export const MIN_BLOCKABLE_ITEMS = 2;

/** Where the one-offs pool; the tree draws this node. */
export const RARITY_POOL_LABEL = "Other";

/** Banding for attributes with no magnitude: values repeated often enough to hold a
 *  block keep themselves, the one-offs pool. Only pooled values are mapped, since
 *  `distinctValueRatio` leaves anything unmapped as itself. */
export const deriveRarityBands = (
  values: readonly (string | null | undefined)[],
): Record<string, string> => {
  const counts = new Map<string, number>();
  for (const raw of values) {
    if (raw == null) continue;
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  }
  const bands: Record<string, string> = {};
  for (const [value, count] of counts) {
    if (count < MIN_BLOCKABLE_ITEMS) bands[value] = RARITY_POOL_LABEL;
  }
  // Everything pooled means the attribute is unique per item, not bandable.
  return Object.keys(bands).length === counts.size ? {} : bands;
};

/** Share of the carrying products that are alone in their value inside their `parentAttr`
 *  value: 1 for an item code, near 0 for a level whose values each hold a few siblings. */
const loneShareWithin = (
  items: readonly StudioItem[],
  attr: string,
  parentAttr: string,
): number => {
  const counts = new Map<string, number>();
  for (const item of items) {
    const value = item.attrs[attr];
    if (value == null) continue;
    const key = `${item.attrs[parentAttr] ?? ""}${PATH_SEPARATOR}${value}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let carried = 0;
  let lone = 0;
  for (const count of counts.values()) {
    carried += count;
    if (count === 1) lone += 1;
  }
  return carried > 0 ? lone / carried : 1;
};

/** Share of `attr`'s values found under one `parentAttr` value only: 1 when it nests. */
const nestedShare = (items: readonly StudioItem[], attr: string, parentAttr: string): number => {
  const parents = new Map<string, Set<string>>();
  for (const item of items) {
    const value = item.attrs[attr];
    const parent = item.attrs[parentAttr];
    if (value == null || parent == null) continue;
    const seen = parents.get(value) ?? new Set<string>();
    seen.add(parent);
    parents.set(value, seen);
  }
  let nested = 0;
  for (const seen of parents.values()) if (seen.size === 1) nested += 1;
  return parents.size > 0 ? nested / parents.size : 0;
};

/** A many-valued level that nests inside a coarse level (each value under one parent value,
 *  up to NEAR_UNIQUE_RATIO of strays) is a sub-level of it, not an item code, when few of its
 *  products are alone in their value there. Toilet paper: 21 sub-lines over 47 products, 2-4
 *  per brand, 9 products alone in theirs. */
const isSubLevelOfCoarse = (
  items: readonly StudioItem[],
  attr: string,
  coarse: readonly string[],
): boolean => {
  let parent: string | undefined;
  let best = -1;
  for (const parentAttr of coarse) {
    const share = nestedShare(items, attr, parentAttr);
    if (share > best) [parent, best] = [parentAttr, share];
  }
  return (
    parent !== undefined &&
    best >= 1 - NEAR_UNIQUE_RATIO &&
    loneShareWithin(items, attr, parent) <= NEAR_UNIQUE_RATIO
  );
};

/** A near-unique attribute is excluded only if still near-unique after banding and not a
 *  sub-level of a coarse attribute (`isSubLevelOfCoarse`). */
export const quantizeCandidates = (
  items: readonly StudioItem[],
  candidates: readonly AttributeCandidate[],
): LevelQuantization => {
  const levelBands: Record<string, Record<string, string>> = {};
  const excluded: string[] = [];
  if (items.length < MIN_ITEMS_TO_JUDGE_GENERICITY) return { levelBands, excluded };
  // Levels a near-unique one may nest in: several values, each holding many products.
  const coarse = candidates
    .map((candidate) => candidate.attr)
    .filter(
      (attr) =>
        new Set(items.map((item) => item.attrs[attr]).filter((value) => value != null)).size > 1 &&
        distinctValueRatio(items, attr) <= NEAR_UNIQUE_RATIO,
    );
  for (const candidate of candidates) {
    const carrying = items.filter((item) => item.attrs[candidate.attr] != null).length;
    if (carrying < MIN_ITEMS_TO_JUDGE_GENERICITY) continue;
    if (distinctValueRatio(items, candidate.attr) <= NEAR_UNIQUE_RATIO) continue;
    const { bandOf } = deriveValueBands(items.map((item) => item.attrs[candidate.attr]));
    const banded = Object.fromEntries(bandOf);
    if (bandOf.size > 0 && distinctValueRatio(items, candidate.attr, banded) <= NEAR_UNIQUE_RATIO) {
      levelBands[candidate.attr] = banded;
      continue;
    }
    const rarity = deriveRarityBands(items.map((item) => item.attrs[candidate.attr]));
    if (
      Object.keys(rarity).length > 0 &&
      distinctValueRatio(items, candidate.attr, rarity) <= NEAR_UNIQUE_RATIO
    ) {
      levelBands[candidate.attr] = rarity;
      continue;
    }
    if (isSubLevelOfCoarse(items, candidate.attr, coarse)) continue;
    excluded.push(candidate.attr);
  }
  return { levelBands, excluded };
};

const bandedView = (
  items: readonly StudioItem[],
  levelBands: Record<string, Record<string, string>>,
): readonly StudioItem[] => {
  const attrs = Object.keys(levelBands);
  if (attrs.length === 0) return items;
  return items.map((item) => {
    const next = { ...item.attrs };
    let touched = false;
    for (const attr of attrs) {
      const raw = next[attr];
      if (raw == null) continue;
      const band = levelBands[attr][raw];
      if (band !== undefined && band !== raw) {
        next[attr] = band;
        touched = true;
      }
    }
    return touched ? { ...item, attrs: next } : item;
  });
};

const BAND_SHARE_THRESHOLD = 0.1;

export const annotateContract = (
  items: readonly StudioItem[],
  spine: readonly SpineLevel[],
  runs: readonly StudioRunGeometry[],
  out: Pick<InferredSpine, "scopedValueOrder" | "bandOverrides" | "orientOverrides">,
  levelIndex = 0,
  path: readonly string[] = [],

  bandShareThreshold: number = BAND_SHARE_THRESHOLD,
  inferenceOptions: Pick<
    InferenceOptions,
    | "fragmentJoinPct"
    | "firstPreferredValueMatch"
    | "lastPreferredValueMatch"
    | "firstPreferredValues"
    | "lastPreferredValues"
  > = {},

  scopedLevelOrder?: Readonly<Record<string, readonly string[]>>,
  /** Set on a horizontally blocked wall: every scope orders its values by the walk. */
  walkRuns?: readonly StudioRunGeometry[],
) => {
  const runOffsets = runOffsetsOf(runs);
  const join = fragmentJoinOf(runs, inferenceOptions.fragmentJoinPct);
  walkSplitScopes(
    items,
    spine,
    scopedLevelOrder,
    (item, attr) => item.attrs[attr] ?? "",
    ({ level, path: scopePath, entries, groups }) => {
      const pathKey = scopePath.join(PATH_SEPARATOR);
      const measured = measureOrientation([...groups.values()], runOffsets);
      if (measured.orientation !== level.orient) {
        out.orientOverrides[pathKey] = measured.orientation;
      }
      const order = walkRuns
        ? inferWalkValueOrder(entries, level.attr, walkRuns, inferenceOptions)
        : measured.orientation === "vertical"
          ? inferVerticalValueOrder(entries, level.attr, runOffsets)
          : measured.xSeparation > 0.5
            ? valueOrderByStretch(entries, level.attr, runOffsets, join, inferenceOptions)
            : inferReadingOrder(entries, level.attr);
      out.scopedValueOrder[pathKey] = order;
      if (measured.orientation !== "vertical") return;
      for (const [value, group] of groups) {
        const widths = new Map<number, number>();
        let total = 0;
        for (const item of group) {
          for (const placement of item.placements) {
            widths.set(placement.shelf, (widths.get(placement.shelf) ?? 0) + placement.widthCm);
            total += placement.widthCm;
          }
        }
        const band = [...widths.entries()]
          .filter(([, width]) => width >= total * bandShareThreshold)
          .map(([rank]) => rank)
          .sort((a, b) => a - b);
        if (band.length > 0) {
          out.bandOverrides[[...scopePath, value].join(PATH_SEPARATOR)] = band;
        }
      }
    },
    levelIndex,
    path,
  );
};

const inferOverView = (
  items: readonly StudioItem[],
  candidates: readonly AttributeCandidate[],
  runs: readonly StudioRunGeometry[],
  options: InferenceOptions = {},
): InferredSpine => {
  const tieMargin = options.tieMargin ?? DEFAULT_TIE_MARGIN;
  const verticalBlockShare = options.verticalBlockShare ?? VERTICAL_BLOCK_SHARE;
  if (options.legacyFragmentJoin !== undefined && options.fragmentJoinPct === undefined) {
    // Its meaning depends on the plan's unit: `withPlanFragmentJoin` reads it first.
    throw new Error("A legacy fragment join must be read against its plan before inference");
  }
  const join = fragmentJoinOf(runs, options.fragmentJoinPct);
  const preferred = options.preferredOrder ?? candidates.map((candidate) => candidate.attr);
  const runOffsets = runOffsetsOf(runs);

  const valueOrder: Record<string, string[]> = {};
  for (const candidate of candidates) {
    valueOrder[candidate.attr] = valueOrderByStretch(
      items,
      candidate.attr,
      runOffsets,
      join,
      options,
    );
  }

  const orientOverrides: Record<string, Orientation> = {};

  const rank = (
    initialScopes: readonly ScopedGroup[],
    pool: readonly AttributeCandidate[],
    depthOffset: number,
    limit?: number,
  ): { levels: SpineLevel[]; decisive: boolean[] } => {
    let scopes: ScopedGroup[] = [...initialScopes];
    const remaining = [...pool];
    const spine: SpineLevel[] = [];
    const decisive: boolean[] = [];
    while (remaining.length > 0 && (limit === undefined || spine.length < limit)) {
      const scored = remaining.map((candidate) => {
        let extra = 0;
        let valuePairs = 0;
        let differentiates = false;

        // Bands affect score only; scopes and shipped values stay raw.
        const bandOf = options.scoreBands?.[candidate.attr];
        for (const scope of scopes) {
          const values = new Set(scope.items.map((item) => item.attrs[candidate.attr] ?? ""));
          if (values.size < 2) continue;
          differentiates = true;
          extra += measureExtraFragments(scope.items, candidate.attr, bandOf, join);
          valuePairs +=
            (bandOf ? new Set([...values].map((v) => bandOf[v] ?? v)).size : values.size) - 1;
        }

        return {
          candidate,
          score: differentiates ? -extra / Math.max(valuePairs, 1) : 0,
          differentiates,
        };
      });
      options.trace?.(
        depthOffset + spine.length,
        scored.map((entry) => ({ attr: entry.candidate.attr, score: entry.score })),
      );
      const eligible = new Set(
        rungContenders(
          remaining,
          preferred,
          options.quantityAttribute,
          // Raw size is the sequencing level: every attribute that still splits a scope at
          // this depth takes its rung first. Only size TIER is a tier.
          (attr) => scored.find((entry) => entry.candidate.attr === attr)?.differentiates ?? false,
        ).map((candidate) => candidate.attr),
      );
      const inContest = declaredContenders(
        scored.filter((entry) => eligible.has(entry.candidate.attr)),
        remaining,
        options.declaredLevelOrder,
        scopes.map((scope) => scope.items),
      );
      let best = inContest[0];
      for (const entry of inContest) if (entry.score > best.score) best = entry;

      const slack = Math.max(0.15, Math.abs(best.score) * tieMargin);
      // With a pinned quantity, every block-worthy splitter is a contender and the declared
      // order decides among them (see rankBranch). Attributes that split nothing here score 0
      // but are not splitters, so they no longer outrank real levels.
      // Worthiness is judged at section level (as for the pin): deep in the tree the scopes
      // are tiny and a jumbled attribute looks contiguous, which would let Detergent's
      // sub-brand claim a rung above size on declaration alone.
      const rootAttr = spine[0]?.attr ?? options.rootAttr;
      const worthy =
        options.quantityAttribute === undefined || rootAttr === undefined
          ? []
          : inContest.filter(
              (entry) =>
                entry.differentiates &&
                sectionBlockWorthy(
                  items,
                  rootAttr,
                  entry.candidate.attr,
                  options.scoreBands?.[entry.candidate.attr],
                  join,
                ),
            );
      const contenders =
        worthy.length > 0 ? worthy : inContest.filter((entry) => entry.score >= best.score - slack);
      const preferredIndex = (attr: string) => {
        const index = preferred.indexOf(attr);
        return index < 0 ? preferred.length : index;
      };

      const valueCountOf = (attr: string) =>
        new Set(items.map((item) => item.attrs[attr] ?? "")).size;
      const brandAttr = options.brandAttr;
      const brandCount =
        brandAttr === undefined
          ? 0
          : new Set(
              items
                .map((item) => item.attrs[brandAttr])
                .filter((brand) => !isPlaceholderValue(brand)),
            ).size;
      // A root split far finer than the brands (a scent read as CDT0) is not the category's top
      // level; the brand role takes it, but only when every product has a brand to stand under.
      const configuredRoot =
        options.rootAttr !== undefined &&
        brandAttr !== undefined &&
        brandAttr !== options.rootAttr &&
        scored.some((entry) => entry.candidate.attr === brandAttr) &&
        brandCount > 1 &&
        items.every((item) => !isPlaceholderValue(item.attrs[brandAttr])) &&
        valueCountOf(options.rootAttr) > ROOT_FRAGMENTS_PER_BRAND * brandCount
          ? brandAttr
          : options.rootAttr;
      const assertedRoot =
        depthOffset + spine.length === 0 &&
        configuredRoot !== undefined &&
        scored.some(
          (entry) => entry.candidate.attr === configuredRoot && valueCountOf(configuredRoot) > 1,
        )
          ? configuredRoot
          : undefined;
      const forcedAttr = options.forcedOrder?.[depthOffset + spine.length]?.attr ?? assertedRoot;
      const winner =
        (forcedAttr !== undefined
          ? scored.find((entry) => entry.candidate.attr === forcedAttr)
          : undefined) ??
        pickByEvidence(
          contenders,
          preferredIndex,
          scopes.map((scope) => scope.items),
          (rungScopes, attr) =>
            groupingEvidenceOf(rungScopes, attr, options.scoreBands?.[attr], join),
          (attr) => groupingEvidenceOf([items], attr, options.scoreBands?.[attr], join).evidence,
        );

      const measures: { path: readonly string[]; measure: OrientationMeasure }[] = [];
      for (const scope of scopes) {
        const groups = splitScope(scope, winner.candidate.attr, valueOrder[winner.candidate.attr]);
        if (groups.length < 2) continue;
        measures.push({
          path: scope.path,
          measure: measureOrientation(
            groups.map((group) => group.items),
            runOffsets,
          ),
        });
      }
      const horizontalVotes = measures.filter((m) => m.measure.orientation === "horizontal").length;

      const valueGroups: StudioItem[][] = [];
      for (const scope of scopes) {
        const groups = splitScope(scope, winner.candidate.attr, valueOrder[winner.candidate.attr]);
        if (groups.length < 2) continue;
        for (const group of groups) valueGroups.push([...group.items]);
      }
      const shelvesPerRun = new Map<number, number>(
        runs.map((run) => [run.run, run.shelves.length]),
      );
      const topToBottomShare = measureTopToBottomShare(valueGroups, shelvesPerRun);
      const defaultOrient: Orientation =
        topToBottomShare >= verticalBlockShare
          ? "horizontal"
          : horizontalVotes >= measures.length - horizontalVotes
            ? "horizontal"
            : "vertical";
      if (defaultOrient === "vertical") {
        valueOrder[winner.candidate.attr] = inferVerticalValueOrder(
          items,
          winner.candidate.attr,
          runOffsets,
        );
      }

      spine.push({
        attr: winner.candidate.attr,
        label: winner.candidate.label,
        orient: defaultOrient,
      });

      decisive.push(forcedAttr === undefined && contenders.length === 1);
      remaining.splice(
        remaining.findIndex((candidate) => candidate.attr === winner.candidate.attr),
        1,
      );
      scopes = scopes.flatMap((scope) => {
        const groups = splitScope(scope, winner.candidate.attr, valueOrder[winner.candidate.attr]);
        return groups.length < 2 ? [scope] : groups;
      });
    }
    return { levels: spine, decisive };
  };

  const spine = rank([{ path: [], items }], candidates, 0).levels;
  const scopedLevelOrder: Record<string, string[]> = {};

  /* A horizontal wall (brands own rows; inside a brand, forms and lines own rows): a brand
     whose order would open on a level that is not a row (Dove's 43-to-1 packaging, a lone
     twin pack) opens on its rows instead, coarsest first; the levels below keep the order
     the branch ranking gives them, and a brand already opening on a row keeps its order.
     The wall reads by the brand, or a level above it, that cuts it into real blocks and
     reads one way (`wallBranchOf`), else by the first level that splits it: a constant
     section above the brand names nothing. Brands are reordered by their rows only where
     that level is the first to split the wall, since only there is a brand a branch of the
     tree. Applied after the branch ranking below. See `horizontalBlocking.ts`. */
  const horizontalRows = new Map<
    string,
    { rows: string[]; planOrder: string[]; items: readonly StudioItem[] }
  >();
  const splitDepth = spine.findIndex(
    (level) => new Set(items.map((item) => item.attrs[level.attr] ?? "")).size > 1,
  );
  const wallLevels = spine
    .map((level) => level.attr)
    .filter((attr) => attr !== options.quantityAttribute);
  const branchAttr =
    wallBranchOf(items, wallLevels, runs, options.brandAttr) ?? spine[splitDepth]?.attr;
  const branchDepth = spine.findIndex((level) => level.attr === branchAttr);
  const branchLevel = branchDepth >= 0 ? spine[branchDepth] : undefined;
  let wallBlocking: WallBlocking | undefined;
  if (branchLevel && splitDepth + 1 < spine.length) {
    const planOrder = spine.slice(branchDepth + 1).map((level) => level.attr);
    const rowCandidates = wallLevels.filter((attr) => attr !== branchLevel.attr);
    const wall = readHorizontalWall(items, branchLevel.attr, rowCandidates, runs);
    const read = wall.horizontal ? "horizontal" : "vertical";
    // The merchant's flip, on an untouched draft, wins; the read stays recorded.
    const blocking = options.wallBlocking ?? read;
    // Read once, here, on both paths: everything downstream takes this reading from the
    // draft, and the studio states it so the merchant can flip it.
    const reading: WallBlocking = {
      blocking,
      read,
      branchAttr: branchLevel.attr,
      branchScore: wall.branchScore,
      branchBand: wall.branchBand,
      share: wall.share,
      branches: Object.fromEntries(wall.branches),
      eligible: wall.eligible,
    };
    wallBlocking = reading;
    if (blocking === "horizontal") {
      // Sequence by the walk at every level: bay, shelf from the top, left to right.
      for (const candidate of candidates) {
        valueOrder[candidate.attr] = inferWalkValueOrder(items, candidate.attr, runs, options);
      }
    }
    if (blocking === "horizontal" && branchDepth === splitDepth) {
      const prefix = spine.slice(0, branchDepth).map((level) => items[0]?.attrs[level.attr] ?? "");
      const rankOf = preferredRankOf(preferred);
      for (const branch of splitScope(
        { path: prefix, items },
        branchLevel.attr,
        valueOrder[branchLevel.attr] ?? [],
      )) {
        const value = branch.path[branch.path.length - 1];
        if (isPlaceholderValue(value) || !branchStacksRows(reading, value)) continue;
        const rows = rowLevelsOf(branch.items, rowCandidates, runs, rankOf);
        if (rows.length > 0) {
          horizontalRows.set(branch.path.join(PATH_SEPARATOR), {
            rows,
            planOrder,
            items: branch.items,
          });
        }
      }
    }
  }

  const rootLevel = spine[0];
  const belowRoot = candidates.filter((candidate) => candidate.attr !== rootLevel?.attr);
  const rootBranches = rootLevel
    ? splitScope({ path: [], items }, rootLevel.attr, valueOrder[rootLevel.attr])
    : [];
  if (rootLevel && rootBranches.length > 1 && belowRoot.length > 0) {
    const planOrder = spine.slice(1).map((level) => level.attr);
    const sizeLevels = sizeLevelsOf(items, belowRoot, options.quantityAttribute);
    for (const branch of rootBranches) {
      // "" is read at depth 0 of every path; it is not a branch key.
      if (branch.path.some((value) => isPlaceholderValue(value))) continue;
      const branchOrder = rankBranch(
        branch.items,
        belowRoot,
        preferred,
        join,
        options.quantityAttribute,
        options.declaredLevelOrder,
        items,
      );
      if (!branchOrder) continue;
      const own = treeCost(branch.items, branchOrder.attrs, join);
      const plan = treeCost(branch.items, planOrder, join);

      const refined =
        own === plan && branch.items.length >= MIN_ITEMS_TO_JUDGE_GENERICITY
          ? refineBranchOrder(branch.items, branchOrder.attrs, join, options.declaredLevelOrder)
          : { attrs: [...branchOrder.attrs], cost: own };
      // The adjacent-swap search may lift the quantity level back over a block level; re-seat it.
      // Sparse levels are not re-seated here: the root branch keeps the order it had before
      // `seatSparseLevels`, which only re-seats a brand's row levels below.
      const attrs = pinQuantity(
        refined.attrs,
        options.quantityAttribute,
        (attr) => fragmentScoreOf(branch.items, attr, options.scoreBands?.[attr], join) !== null,
        sizeLevels,
      );
      const chosen =
        attrs.join(PATH_SEPARATOR) === refined.attrs.join(PATH_SEPARATOR)
          ? refined
          : { attrs, cost: treeCost(branch.items, attrs, join) };
      if (chosen.attrs.join(PATH_SEPARATOR) === planOrder.join(PATH_SEPARATOR)) continue;

      // On a tie without stronger branch evidence, move only size levels; unrelated levels
      // keep the plan's order.
      const opening = (order: readonly string[]) =>
        order.find((attr) => new Set(branch.items.map((item) => item.attrs[attr] ?? "")).size > 1);
      const isSize = (attr: string | undefined) =>
        attr !== undefined && (attr === options.quantityAttribute || sizeLevels.has(attr));
      const pinnedPlan = pinQuantity(
        planOrder,
        options.quantityAttribute,
        (attr) => fragmentScoreOf(branch.items, attr, options.scoreBands?.[attr], join) !== null,
        sizeLevels,
      );
      const branchWonBeforePinning =
        refined.cost < plan &&
        chosen.cost === plan &&
        isSize(opening(planOrder)) &&
        !isSize(opening(chosen.attrs));
      if (chosen.cost < plan || branchWonBeforePinning) {
        scopedLevelOrder[branch.path.join(PATH_SEPARATOR)] = chosen.attrs;
        continue;
      }
      // A plan that opens this branch on pack size may seat size below its block levels
      // without increasing tree cost. Preserve the plan's other level ordering on a tie.
      if (
        isSize(opening(planOrder)) &&
        !isSize(opening(pinnedPlan)) &&
        treeCost(branch.items, pinnedPlan, join) <= plan
      ) {
        scopedLevelOrder[branch.path.join(PATH_SEPARATOR)] = pinnedPlan;
      }
    }
  }

  for (const [key, { rows, planOrder, items: branchItems }] of horizontalRows) {
    const ranked = scopedLevelOrder[key] ?? planOrder;
    // The first level that splits this brand, as the tree would read it.
    const opening = ranked.find(
      (attr) => new Set(branchItems.map((item) => item.attrs[attr] ?? "")).size > 1,
    );
    if (opening !== undefined && isRowLevel(branchItems, opening, runs)) continue;
    // A row level the branch reads off a few products (the pods with a sub-brand) is sparse like
    // any other level: it stays where the plan puts it, under the brand.
    const order = seatSparseLevels(
      [...rows, ...ranked.filter((attr) => !rows.includes(attr))],
      planOrder,
      branchItems,
    );
    if (order.join(PATH_SEPARATOR) === planOrder.join(PATH_SEPARATOR)) delete scopedLevelOrder[key];
    else scopedLevelOrder[key] = order;
  }

  const annotations: Pick<InferredSpine, "scopedValueOrder" | "bandOverrides" | "orientOverrides"> =
    { scopedValueOrder: {}, bandOverrides: {}, orientOverrides };
  annotateContract(
    items,
    spine,
    runs,
    annotations,
    0,
    [],
    BAND_SHARE_THRESHOLD,
    options,
    scopedLevelOrder,
    isHorizontalWall({ wallBlocking }) ? runs : undefined,
  );

  const shelfHints: Record<string, { shelf: number; hFacings: number }[]> = {};
  for (const item of items) {
    shelfHints[item.skuId] = item.placements.map((placement) => ({
      shelf: placement.shelf,
      hFacings: placement.hFacings,
    }));
  }

  return {
    spine,
    scopedLevelOrder,
    valueOrder,
    scopedValueOrder: annotations.scopedValueOrder,
    bandOverrides: annotations.bandOverrides,
    orientOverrides,
    shelfHints,
    levelBands: {},
    ...(wallBlocking ? { wallBlocking } : {}),
  };
};

/** Attributes the base gives no evidence for as a level, which therefore never become one:
 *
 *  - one value per product (an item number, a UPC, a free-text size that bands could not
 *    group): no two products share a value, so their order on the base says nothing about
 *    how products group;
 *  - a live column the configuration does not name that groups the products exactly as the
 *    asserted root does (Sam's Club's `section` column beside the `variant` that carries the
 *    same subcategories): the merchant would see the subcategory level twice.
 *
 *  The root and the quantity attribute are never dropped: the root is given, and the quantity
 *  attribute sequences rather than blocks. Bands, where quantization found them, count as the
 *  values. */
export const levelsWithoutEvidence = (
  items: readonly StudioItem[],
  candidates: readonly AttributeCandidate[],
  options: Pick<InferenceOptions, "rootAttr" | "quantityAttribute" | "preferredOrder">,
  bands: Readonly<Record<string, Readonly<Record<string, string>>>> = {},
): string[] => {
  const kept = new Set([options.rootAttr, options.quantityAttribute]);
  // As in `inferOverView`: without a configured order, the candidates' own order is the one.
  const named = new Set(options.preferredOrder ?? candidates.map((candidate) => candidate.attr));
  const valueOf = (item: StudioItem, attr: string): string | null => {
    const raw = item.attrs[attr];
    if (raw == null || isPlaceholderValue(raw)) return null;
    return bands[attr]?.[raw] ?? raw;
  };
  /** The grouping as a key: each product's value, renamed by first appearance. */
  const groupingOf = (attr: string) => {
    const names = new Map<string | null, number>();
    return items
      .map((item) => {
        const value = valueOf(item, attr);
        if (!names.has(value)) names.set(value, names.size);
        return names.get(value);
      })
      .join(",");
  };
  const rootGrouping = options.rootAttr === undefined ? undefined : groupingOf(options.rootAttr);
  const onePerProduct: string[] = [];
  const twinsOfRoot: string[] = [];
  for (const { attr } of candidates) {
    const counts = new Map<string, number>();
    for (const item of items) {
      const value = valueOf(item, attr);
      if (value !== null) counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    if (counts.size < 2) continue;
    const unique = [...counts.values()].every((count) => count === 1);
    if (kept.has(attr)) continue;
    if (unique) onePerProduct.push(attr);
    else if (!named.has(attr) && groupingOf(attr) === rootGrouping) twinsOfRoot.push(attr);
  }
  return [...onePerProduct, ...twinsOfRoot];
};

export const inferSpine = (
  items: readonly StudioItem[],
  allCandidates: readonly AttributeCandidate[],
  runs: readonly StudioRunGeometry[],
  options: InferenceOptions = {},
): InferredSpine => {
  const rows = compactRowsOf(runs, items);
  const quantized = inferQuantized(rows.items, allCandidates, rows.runs, options);
  const inferred = options.quantityAttribute
    ? { ...quantized, quantityAttribute: options.quantityAttribute }
    : quantized;
  if (!rows.active) return inferred;
  return {
    ...inferred,
    bandOverrides: Object.fromEntries(
      Object.entries(inferred.bandOverrides).map(([key, band]) => [key, band.map(rows.rankOf)]),
    ),
    shelfHints: Object.fromEntries(
      Object.entries(inferred.shelfHints).map(([skuId, hints]) => [
        skuId,
        hints.map((hint) => ({ ...hint, shelf: rows.rankOf(hint.shelf) })),
      ]),
    ),
  };
};

const inferQuantized = (
  items: readonly StudioItem[],
  allCandidates: readonly AttributeCandidate[],
  runs: readonly StudioRunGeometry[],
  options: InferenceOptions,
): InferredSpine => {
  markBayEdgeNeighbours(items, runs);
  if (options.quantize === false) {
    const dropped = levelsWithoutEvidence(items, allCandidates, options);
    const candidates = allCandidates.filter((candidate) => !dropped.includes(candidate.attr));
    return inferOverView(items, candidates, runs, options);
  }
  const { levelBands, excluded } = quantizeCandidates(items, allCandidates);
  const dropped = levelsWithoutEvidence(items, allCandidates, options, levelBands);
  const candidates = allCandidates.filter((candidate) => !dropped.includes(candidate.attr));

  const usable = candidates.filter((candidate) => !excluded.includes(candidate.attr));
  if (Object.keys(levelBands).length === 0) {
    return inferOverView(items, usable, runs, options);
  }

  // Bands score; raw values ship unless retainBands.
  if (!options.retainBands) {
    return {
      ...inferOverView(items, usable, runs, { ...options, scoreBands: levelBands }),
      levelBands: {},
    };
  }
  const ranked = inferOverView(bandedView(items, levelBands), usable, runs, options);
  const deepest = ranked.spine[ranked.spine.length - 1]?.attr;
  if (deepest === undefined || levelBands[deepest] === undefined) {
    return { ...ranked, levelBands };
  }
  const kept = Object.fromEntries(Object.entries(levelBands).filter(([attr]) => attr !== deepest));
  const final = inferOverView(bandedView(items, kept), usable, runs, options);
  return { ...final, levelBands: kept };
};

export const draftFromInference = (inference: InferredSpine): StudioDraft => ({
  spine: inference.spine,
  // Omit empty maps: drafts are stringified for history signatures.
  ...(Object.keys(inference.scopedLevelOrder ?? {}).length > 0
    ? { scopedLevelOrder: inference.scopedLevelOrder }
    : {}),
  valueOrder: inference.valueOrder,
  scopedValueOrder: inference.scopedValueOrder,
  bandOverrides: inference.bandOverrides,
  orientOverrides: inference.orientOverrides,
  shelfHints: inference.shelfHints,
  ...(inference.wallBlocking ? { wallBlocking: inference.wallBlocking } : {}),
  attrOverrides: {},

  ...(Object.keys(inference.levelBands).length > 0 ? { levelBands: inference.levelBands } : {}),
  ...(inference.quantityAttribute ? { quantityAttribute: inference.quantityAttribute } : {}),
  restrictions: [],
  dismissedFlagIds: [],
  appliedFixFlagIds: [],
});
